import { zodResolver } from '@hookform/resolvers/zod';
import {
  useCreateInvitation,
  useGetUser,
  useListAccessRules,
  useListRoleAccessRules,
  useUpdateUserAccess,
} from '@sunstone/api-client/hooks';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  Separator,
  Stepper,
} from '@sunstone/ui';
import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import { PageHeader } from '@/components/ui/PageHeader';
import { useAuthToken } from '@/features/auth';
import { AccessRuleListEditor } from '@/features/users/AccessRuleListEditor';
import { AccessSummary } from '@/features/users/AccessSummary';
import {
  accessRuleToDraft,
  type RuleDraft,
  ruleDraftConditionJson,
  ruleDraftHasContent,
  ruleDraftsEqual,
  rulesToAccessRuleInputs,
  splitRulesByKind,
} from '@/features/users/accessRuleDraft';
import { useIsPartnerTenant, useTenantName } from '@/features/users/accessRuleSentence';
import { RolePermissionSummary } from '@/features/users/RolePermissionSummary';
import { RolePickerCombobox } from '@/features/users/RolePickerCombobox';
import { useRolePicker } from '@/features/users/useRolePicker';
import { isFailedPreconditionError } from '@/lib/errors';
import { routes } from '@/lib/routes';

const STEPS = ['Who', 'Access', 'Confirm'];

// email is only collected (and required) at invite time — identity is fixed
// once a user exists, so edit mode disables it instead. name is required in
// edit mode (there's no "clear a name" feature) but optional at invite time,
// where an empty name just means the identity provider's own name claim
// fills it in on first login.
function buildSchema(isEditMode: boolean) {
  return z.object({
    email: isEditMode
      ? z.string()
      : z.string().min(1, 'Email is required').email('Enter a valid email address'),
    name: isEditMode ? z.string().min(1, 'Name is required') : z.string(),
    roleId: z.string().min(1, 'Select a role'),
  });
}
type FormValues = z.infer<ReturnType<typeof buildSchema>>;

/**
 * One wizard for inviting a new user and editing an existing one — a tenant
 * member or a platform user (no tenant), invited or edited (:userId route
 * param present). tenantId presence only decides which roles useRolePicker
 * returns and which tenant_id gets submitted; :userId presence only decides
 * whether the Who step's email is editable and whether Confirm calls
 * CreateInvitation or UpdateUserAccess (name/role/access-rule diff, sent as
 * one request) — never a structurally different UI. The backend
 * (CreateInvitation) already enforces that only an Operator Admin can grant
 * the Operator Admin role and that its tenant_id must be empty; the role
 * picker mirrors that here so a non-Operator-Admin inviter never even sees
 * it as an option.
 *
 * A real 3-step wizard (Who / Access / Confirm) rather than one long form —
 * each step's content is on its own, so the inviter/editor isn't asked to
 * think about resource restrictions before they've even picked a role.
 */
export function UserAccessWizard() {
  const { tenantId, userId } = useParams<{ tenantId?: string; userId?: string }>();
  const isEditMode = !!userId;
  const navigate = useNavigate();
  const { token } = useAuthToken();

  const { data: userData, isLoading: userLoading } = useGetUser(
    isEditMode ? (userId ?? '') : '',
    token,
  );
  const existingUser = userData?.user;
  const { data: rulesData, isLoading: rulesLoading } = useListAccessRules(
    isEditMode ? (userId ?? '') : '',
    token,
  );
  const existingRules = rulesData?.rules ?? [];

  const [step, setStep] = useState(1);
  const [showRestriction, setShowRestriction] = useState(false);
  const [deploymentRules, setDeploymentRules] = useState<RuleDraft[]>([]);
  const [workspaceRules, setWorkspaceRules] = useState<RuleDraft[]>([]);
  const [seeded, setSeeded] = useState(false);
  // Tracks which role id the current deploymentRules/workspaceRules state was
  // populated for — either the initial edit-mode load, or the last role
  // change we reset for. Lets the reset effect below tell "role the user just
  // changed to" apart from "role we just finished loading rules for" without
  // firing on the same render that seeds those rules from existingRules.
  const rulesLoadedForRoleId = useRef<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(buildSchema(isEditMode)),
    mode: 'onChange',
    defaultValues: { email: '', name: '', roleId: '' },
  });

  const email = form.watch('email');
  const name = form.watch('name');
  const roleId = form.watch('roleId');

  useEffect(() => {
    if (isEditMode && !seeded && existingUser && !rulesLoading) {
      form.setValue('email', existingUser.email);
      form.setValue('name', existingUser.name);
      form.setValue('roleId', existingUser.primaryRoleId);
      const split = splitRulesByKind(existingRules);
      setDeploymentRules(split.deployment);
      setWorkspaceRules(split.workspace);
      if (split.deployment.length > 0 || split.workspace.length > 0) setShowRestriction(true);
      rulesLoadedForRoleId.current = existingUser.primaryRoleId;
      setSeeded(true);
    }
  }, [isEditMode, seeded, existingUser, rulesLoading, existingRules, form.setValue]);

  const rolePicker = useRolePicker(tenantId ?? '', token, roleId);
  const selectedRole = rolePicker.selectedRole;
  const isUnrestricted = selectedRole?.isUnrestricted ?? false;
  const tenantName = useTenantName(tenantId ?? '');
  const isPartner = useIsPartnerTenant(tenantId ?? '');

  // Any actual role change — not just becoming unrestricted — invalidates the
  // previous role's access-rule drafts: a restricted role's applicable
  // resource kinds/scope can differ just as much between two restricted
  // roles as between restricted and unrestricted. Skipped on the render that
  // seeds rules for isEditMode's initial load (rulesLoadedForRoleId is set to
  // that same roleId in the same tick, above) and on the very first pick in
  // create mode (rulesLoadedForRoleId starts null, nothing to clear yet).
  useEffect(() => {
    if (!roleId || roleId === rulesLoadedForRoleId.current) return;
    rulesLoadedForRoleId.current = roleId;
    setShowRestriction(false);
    setDeploymentRules([]);
    setWorkspaceRules([]);
  }, [roleId]);

  // The selected role's own access_rules envelope (SUN-408 role-scoped
  // pickers) — resolvable for any role that can own access_rules, i.e. any
  // scope except system (the one seeded unrestricted role never owns
  // access_rules — see role/access.go's requireManageAccessToRole). A
  // platform-scope role's rules resolve against the platform-wide pool
  // (empty tenantId), same convention RoleAccessRulesEditor already uses. A
  // user's own access_rules can only narrow within this, never widen it
  // (accessrule.gateBoth), so the pickers must reflect it too.
  const roleHasAccessRules = !!selectedRole && selectedRole.scope !== 'system';
  const { data: roleRulesData } = useListRoleAccessRules(
    roleHasAccessRules ? (selectedRole?.id ?? '') : '',
    token,
  );
  const roleRules = roleRulesData?.accessRules ?? [];
  const { deployment: roleDeploymentRules, workspace: roleWorkspaceRules } =
    splitRulesByKind(roleRules);

  const createInvitation = useCreateInvitation(token);
  const updateUserAccess = useUpdateUserAccess(token);
  const [isSaving, setIsSaving] = useState(false);
  const submittedRef = useRef(false);

  const restrictionEnabled = showRestriction && !isUnrestricted;
  const hasRestriction =
    restrictionEnabled &&
    (deploymentRules.some(ruleDraftHasContent) || workspaceRules.some(ruleDraftHasContent));

  function cancelRoute() {
    if (isEditMode) {
      return tenantId
        ? routes.userDetail(tenantId, userId ?? '')
        : routes.platformUserDetail(userId ?? '');
    }
    return tenantId ? routes.tenant(tenantId) : routes.platformUsers();
  }

  async function handleNextFromWho() {
    const fields = isEditMode ? (['name', 'roleId'] as const) : (['email', 'roleId'] as const);
    const valid = await form.trigger(fields);
    if (valid) setStep(2);
  }

  function draftToCreateParams(draft: RuleDraft) {
    return {
      effect: draft.effect,
      restrictionKind: draft.restrictionKind,
      resourceIds: draft.restrictionKind === 'id_based' ? draft.resourceIds : undefined,
      ruleConditionJson:
        draft.restrictionKind === 'rule_based' ? ruleDraftConditionJson(draft) : undefined,
      resourceKind: draft.resourceKind,
    };
  }

  async function saveEdits(values: FormValues) {
    if (!userId || !existingUser) return;
    setIsSaving(true);
    try {
      const originalById = new Map(existingRules.map((r) => [r.id, r]));
      const allDrafts = [...deploymentRules, ...workspaceRules];
      const keptIds = new Set(allDrafts.map((d) => d.sourceId).filter((id): id is string => !!id));
      const toRevokeIds = existingRules.filter((r) => !keptIds.has(r.id)).map((r) => r.id);

      const rulesToCreate = [];
      for (const draft of allDrafts) {
        if (!ruleDraftHasContent(draft)) continue;
        const original = draft.sourceId ? originalById.get(draft.sourceId) : undefined;
        const changed = !original || !ruleDraftsEqual(draft, accessRuleToDraft(original));
        if (changed) {
          if (original) toRevokeIds.push(original.id);
          rulesToCreate.push(draftToCreateParams(draft));
        }
      }

      const nameChanged = values.name !== existingUser.name;
      const roleChanged = values.roleId !== existingUser.primaryRoleId;
      const dedupedRevokeIds = [...new Set(toRevokeIds)];

      if (nameChanged || roleChanged || rulesToCreate.length > 0 || dedupedRevokeIds.length > 0) {
        await updateUserAccess.mutateAsync({
          userId,
          name: nameChanged ? values.name : undefined,
          roleId: roleChanged ? values.roleId : undefined,
          rulesToCreate,
          ruleIdsToRevoke: dedupedRevokeIds,
        });
      }

      toast.success('Access updated');
      navigate(cancelRoute());
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save access changes.');
    } finally {
      setIsSaving(false);
    }
  }

  function sendInvitation(values: FormValues) {
    if (submittedRef.current) return;
    submittedRef.current = true;

    createInvitation.mutate(
      {
        email: values.email.trim(),
        name: values.name.trim(),
        roleId: values.roleId,
        tenantId: tenantId ?? '',
        accessRules: hasRestriction
          ? [
              ...rulesToAccessRuleInputs(deploymentRules),
              ...rulesToAccessRuleInputs(workspaceRules),
            ]
          : [],
      },
      {
        onSuccess: () => {
          toast.success(`Invitation sent to ${values.email.trim()}`);
          navigate(cancelRoute());
        },
        onError: (err) => {
          submittedRef.current = false;
          if (isFailedPreconditionError(err)) {
            // The account row was still committed server-side even though
            // Auth0 rejected the invitation email — say so, rather than the
            // generic message below, which would wrongly imply nothing
            // happened.
            toast.error(
              `Account created for ${values.email.trim()}, but the invite email failed to send. Find them in the list and use Resend.`,
            );
            return;
          }
          toast.error(err.message ?? 'Failed to send invitation.');
        },
      },
    );
  }

  function onSubmit(values: FormValues) {
    if (isEditMode) {
      saveEdits(values);
    } else {
      sendInvitation(values);
    }
  }

  if (isEditMode && (userLoading || !existingUser || rulesLoading)) {
    return (
      <div className="flex min-h-[200px] items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  }

  const isSubmitting = isEditMode ? isSaving : createInvitation.isPending;

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <PageHeader
        title={
          isEditMode
            ? `Edit access — ${existingUser?.name || existingUser?.email}`
            : tenantId
              ? 'Invite member'
              : 'Invite platform user'
        }
        description={
          isEditMode
            ? "Change this member's name, role, and access — changes take effect immediately on save."
            : tenantId
              ? "Give someone access to this customer's data."
              : 'Give someone platform-wide access, with any role you can grant.'
        }
      />

      <Stepper steps={STEPS} current={step} />

      <Card className="max-w-2xl">
        <CardContent className="py-6">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && step < 3) e.preventDefault();
              }}
              className="space-y-6"
            >
              {!isEditMode && createInvitation.isError && (
                <Alert variant="destructive">
                  <AlertDescription>
                    {createInvitation.error?.message ?? 'Failed to send invitation.'}
                  </AlertDescription>
                </Alert>
              )}

              {step === 1 && (
                <div className="space-y-6">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Email {!isEditMode && <span className="text-destructive">*</span>}
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="email"
                            placeholder="dave@contractor.com"
                            autoFocus={!isEditMode}
                            disabled={isEditMode}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Name {isEditMode && <span className="text-destructive">*</span>}
                        </FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Dave Contractor" autoFocus={isEditMode} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="roleId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Role <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <RolePickerCombobox
                            value={field.value}
                            onChange={field.onChange}
                            rolePicker={rolePicker}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {selectedRole && (
                    <RolePermissionSummary
                      role={selectedRole}
                      scopeLabel={tenantId ? 'this customer' : 'the entire platform'}
                      variant="compact"
                    />
                  )}
                </div>
              )}

              {step === 2 && (
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-sm">Narrow this person's access</CardTitle>
                        <div className="text-xs text-muted-foreground mt-0.5 space-y-1">
                          <p>
                            Off — {selectedRole?.name ?? 'the role'}'s permissions apply everywhere
                            they normally would.
                          </p>
                          <p>
                            On — access is limited to only the resources listed below; nothing else,
                            even if {selectedRole?.name ?? 'the role'} allows it.
                          </p>
                          <p>
                            Either way, this only narrows what {selectedRole?.name ?? 'the role'}{' '}
                            grants — never adds to it.
                            {roleHasAccessRules && roleRules.length > 0 && (
                              <>
                                {' '}
                                {selectedRole?.name} already narrows access to {roleRules.length}{' '}
                                resource{roleRules.length === 1 ? '' : 's'} — this can only narrow
                                further.
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                      {!isUnrestricted && (
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="show-restriction"
                            checked={showRestriction}
                            onCheckedChange={(checked) => setShowRestriction(checked === true)}
                          />
                          <Label
                            htmlFor="show-restriction"
                            className="text-sm font-normal cursor-pointer"
                          >
                            Enable
                          </Label>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {isUnrestricted ? (
                      <p className="text-sm text-muted-foreground">
                        {selectedRole?.name} always grants full access — it can't be narrowed to
                        specific resources.
                      </p>
                    ) : restrictionEnabled ? (
                      <div className="space-y-4">
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-2">
                            Deployments
                          </p>
                          <AccessRuleListEditor
                            tenantId={tenantId ?? ''}
                            resourceKind="deployment"
                            drafts={deploymentRules}
                            onChange={setDeploymentRules}
                            allowEffectToggle
                            roleRules={rulesToAccessRuleInputs(roleDeploymentRules)}
                          />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-2">
                            Workspaces
                          </p>
                          <AccessRuleListEditor
                            tenantId={tenantId ?? ''}
                            resourceKind="workspace"
                            drafts={workspaceRules}
                            onChange={setWorkspaceRules}
                            allowEffectToggle
                            roleRules={[
                              ...rulesToAccessRuleInputs(roleDeploymentRules),
                              ...rulesToAccessRuleInputs(roleWorkspaceRules),
                            ]}
                            userRules={rulesToAccessRuleInputs(deploymentRules)}
                          />
                        </div>
                      </div>
                    ) : null}
                    <Separator />
                    <AccessSummary
                      mode="role-and-user"
                      tenantId={tenantId ?? ''}
                      tenantName={tenantName}
                      isPartner={isPartner}
                      hasRole={!!selectedRole}
                      roleDeploymentRules={roleDeploymentRules}
                      roleWorkspaceRules={roleWorkspaceRules}
                      userDeploymentRules={restrictionEnabled ? deploymentRules : []}
                      userWorkspaceRules={restrictionEnabled ? workspaceRules : []}
                    />
                  </CardContent>
                </Card>
              )}

              {step === 3 && (
                <div className="space-y-3">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">
                        {isEditMode ? 'Review changes' : 'Review before sending'}
                      </CardTitle>
                      {!isEditMode && (
                        <p className="text-xs text-muted-foreground">
                          Double-check the invitee, role, and access — you can adjust access later
                          from their member page.
                        </p>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs font-medium text-muted-foreground">Name</p>
                          <p className="text-sm">{name || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-muted-foreground">Email</p>
                          <p className="text-sm">{email}</p>
                        </div>
                      </div>

                      <Separator />

                      {selectedRole && (
                        <RolePermissionSummary
                          role={selectedRole}
                          scopeLabel={tenantId ? 'this customer' : 'the platform'}
                          variant="detailed"
                        />
                      )}

                      <Separator />

                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">Access</p>
                        <AccessSummary
                          mode="role-and-user"
                          tenantId={tenantId ?? ''}
                          tenantName={tenantName}
                          isPartner={isPartner}
                          hasRole={!!selectedRole}
                          roleDeploymentRules={roleDeploymentRules}
                          roleWorkspaceRules={roleWorkspaceRules}
                          userDeploymentRules={restrictionEnabled ? deploymentRules : []}
                          userWorkspaceRules={restrictionEnabled ? workspaceRules : []}
                        />
                      </div>
                    </CardContent>
                  </Card>
                  {!isEditMode && (
                    <p className="text-xs text-muted-foreground">
                      Sending creates the account and emails an invite link — access starts once
                      they accept.
                    </p>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between gap-3 pt-2">
                {step > 1 ? (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setStep(step - 1)}>
                    ← Back
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(cancelRoute())}
                  >
                    Cancel
                  </Button>
                )}

                {step < 3 ? (
                  <Button
                    type="button"
                    size="sm"
                    onClick={step === 1 ? handleNextFromWho : () => setStep(3)}
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    disabled={isSubmitting}
                    onClick={form.handleSubmit(onSubmit)}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        {isEditMode ? 'Saving…' : 'Sending…'}
                      </>
                    ) : isEditMode ? (
                      'Save Changes'
                    ) : (
                      'Send Invitation'
                    )}
                  </Button>
                )}
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
